import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../../utils/api';

const POLL_INTERVAL = 15_000;

/**
 * Polls /api/site-intelligence/latest/ for a given website.
 *
 * Returns:
 *   available  – true once a completed snapshot exists
 *   status     – 'none' | 'pending' | 'computing' | 'completed'
 *   progress   – { entities, claims } live counts while computing
 *   loading    – true during the initial fetch
 *   refetch    – manual refetch trigger
 */
export function useIntelligenceStatus(websiteId) {
  const [state, setState] = useState({
    available: null,
    status: null,
    snapshotId: null,
    progress: null,
    loading: true,
  });
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!websiteId) {
      setState({ available: null, status: null, snapshotId: null, progress: null, loading: false });
      return;
    }

    try {
      const res = await apiRequest(
        `/api/site-intelligence/latest/?website_id=${websiteId}`,
        { method: 'GET' },
      );
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();

      setState({
        available: data.available,
        status: data.status,
        snapshotId: data.snapshot_id || null,
        progress: data.progress || null,
        loading: false,
      });
    } catch (err) {
      console.error('useIntelligenceStatus: fetch failed', err);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [websiteId]);

  useEffect(() => {
    fetchStatus();
    return () => clearInterval(timerRef.current);
  }, [fetchStatus]);

  useEffect(() => {
    clearInterval(timerRef.current);

    const shouldPoll =
      state.status === 'pending' || state.status === 'computing';

    if (shouldPoll) {
      timerRef.current = setInterval(fetchStatus, POLL_INTERVAL);
    }

    return () => clearInterval(timerRef.current);
  }, [state.status, fetchStatus]);

  return { ...state, refetch: fetchStatus };
}
